import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { getEffectiveDate } from "@hicya/habits-shared/engine";
import {
  type HabitsState,
  DEFAULT_SETTINGS,
  createInitialState,
  habitsReducer,
  parseDismissedMilestones,
  parseIntSafe,
  parseSettings,
} from "@hicya/habits-shared/context";

import { trackHabitCompleted, trackHabitIncremented, trackHabitSkipped } from "@/lib/analytics";
import { useDatabaseStatus } from "@/db/provider";
import { useSync } from "@/sync/provider";

import type { Category, Completion, Habit } from "@hicya/habits-shared/db";

// Re-export shared types for consumers
export type { AppSettings, HabitsState } from "@hicya/habits-shared/context";

// ─── Boundary hour cache ───

const BOUNDARY_HOUR_KEY = "habits:dayBoundaryHour";

function getSavedBoundaryHour(): number {
  const stored = localStorage.getItem(BOUNDARY_HOUR_KEY);
  return stored !== null ? parseIntSafe(stored, 0, 0, 23) : 0;
}

// ─── Context ───

interface HabitsContextValue {
  state: HabitsState;
  initializationError?: string;
  retryInitialization?: () => void;
  completeHabit: (habitId: string, completionId: string) => Promise<void>;
  incrementHabit: (habitId: string, completionId: string) => Promise<void>;
  skipHabit: (habitId: string, completionId: string) => Promise<void>;
  backfillHabit: (habitId: string, date: string, completionId: string) => Promise<void>;
  undoCompletion: (habitId: string) => Promise<void>;
  createHabit: (habit: Omit<Habit, "created_at" | "updated_at">) => Promise<void>;
  updateHabit: (id: string, fields: Partial<Omit<Habit, "id" | "created_at">>) => Promise<void>;
  archiveHabit: (id: string) => Promise<void>;
  deleteHabit: (id: string) => Promise<void>;
  updateSettings: (key: string, value: string) => Promise<void>;
  updateCompletionNote: (habitId: string, note: string | null) => Promise<void>;
  reorderHabits: (updates: Array<{ id: string; sort_order: number }>) => Promise<void>;
  createCategory: (cat: { name: string; color: string; icon: string }) => Promise<Category>;
  updateCategory: (
    id: string,
    fields: Partial<Pick<Category, "name" | "color" | "icon">>,
  ) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  reorderCategories: (updates: Array<{ id: string; sort_order: number }>) => Promise<void>;
  refresh: (options?: { includeSettings?: boolean }) => Promise<void>;
}

const HabitsContext = createContext<HabitsContextValue | null>(null);

// ─── Provider ───

export function HabitsProvider({ children }: { children: React.ReactNode }) {
  const dbState = useDatabaseStatus();
  const db = dbState.status === "ready" ? dbState.api : null;
  const { triggerSync, lastSyncAt } = useSync();

  const [state, dispatch] = useReducer(habitsReducer, undefined, createInitialState);

  const [initializationError, setInitializationError] = useState<string>();
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const retryInitialization = useCallback(() => setInitializationAttempt((value) => value + 1), []);

  // Initialize when DB becomes ready
  useEffect(() => {
    if (!db) return;
    const dbApi = db;
    let disposed = false;

    async function init() {
      setInitializationError(undefined);
      try {
        const today = getEffectiveDate(
          new Date().toISOString(),
          Intl.DateTimeFormat().resolvedOptions().timeZone,
          getSavedBoundaryHour(),
        );

        const [habits, completions, categories, settingsRows] = await Promise.all([
          dbApi.getActiveHabits(),
          dbApi.getCompletions(today),
          dbApi.getCategories(),
          dbApi.getAllSettings(),
        ]);

        if (!disposed) {
          const settings = parseSettings(settingsRows, today);
          localStorage.setItem(BOUNDARY_HOUR_KEY, String(settings.dayBoundaryHour));
          dispatch({
            type: "INIT_SUCCESS",
            habits,
            completions,
            categories,
            settings,
            today,
          });
        }
      } catch (err) {
        console.error("Failed to initialize habits:", err);
        if (!disposed) setInitializationError("Your habits couldn’t be loaded. Try again.");
      }
    }

    void init();
    return () => {
      disposed = true;
    };
  }, [db, initializationAttempt]);

  const getToday = useCallback(() => {
    return getEffectiveDate(
      new Date().toISOString(),
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      state.settings.dayBoundaryHour,
    );
  }, [state.settings.dayBoundaryHour]);

  const completeHabit = useCallback(
    async (habitId: string, completionId: string) => {
      if (!db) return;
      const today = getToday();
      const now = new Date().toISOString();
      const completion: Completion = {
        id: completionId,
        habit_id: habitId,
        effective_date: today,
        type: "done",
        value: 1,
        note: null,
        created_at: now,
        updated_at: now,
      };
      await db.upsertCompletion(completion, now);
      dispatch({ type: "UPSERT_COMPLETION", completion });
      triggerSync();
      trackHabitCompleted();
    },
    [db, getToday, triggerSync],
  );

  const incrementHabit = useCallback(
    async (habitId: string, completionId: string) => {
      if (!db) return;
      const today = getToday();
      const now = new Date().toISOString();
      const existing = state.completions.find(
        (c) => c.habit_id === habitId && c.effective_date === today,
      );
      const newValue = existing ? existing.value + 1 : 1;
      const completion: Completion = {
        id: existing?.id ?? completionId,
        habit_id: habitId,
        effective_date: today,
        type: "done",
        value: newValue,
        note: null,
        created_at: existing?.created_at ?? now,
        updated_at: now,
      };
      await db.incrementCompletion(habitId, today, now, completionId);
      dispatch({ type: "UPSERT_COMPLETION", completion });
      triggerSync();
      trackHabitIncremented();
    },
    [db, getToday, state.completions, triggerSync],
  );

  const skipHabit = useCallback(
    async (habitId: string, completionId: string) => {
      if (!db) return;
      const today = getToday();
      const now = new Date().toISOString();
      const completion: Completion = {
        id: completionId,
        habit_id: habitId,
        effective_date: today,
        type: "skip",
        value: 0,
        note: null,
        created_at: now,
        updated_at: now,
      };
      await db.upsertCompletion(completion, now);
      dispatch({ type: "UPSERT_COMPLETION", completion });
      triggerSync();
      trackHabitSkipped();
    },
    [db, getToday, triggerSync],
  );

  const backfillHabit = useCallback(
    async (habitId: string, date: string, completionId: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      const completion: Completion = {
        id: completionId,
        habit_id: habitId,
        effective_date: date,
        type: "backfill",
        value: 1,
        note: null,
        created_at: now,
        updated_at: now,
      };
      await db.upsertCompletion(completion, now);
      dispatch({ type: "UPSERT_COMPLETION", completion });
      triggerSync();
    },
    [db, triggerSync],
  );

  const undoCompletion = useCallback(
    async (habitId: string) => {
      if (!db) return;
      const today = getToday();
      const now = new Date().toISOString();
      await db.deleteCompletion(habitId, today, now);
      dispatch({ type: "UNDO_COMPLETION", habitId, effectiveDate: today });
      triggerSync();
    },
    [db, getToday, triggerSync],
  );

  const createHabit = useCallback(
    async (habit: Omit<Habit, "created_at" | "updated_at">) => {
      if (!db) return;
      await db.insertHabit(habit);
      const fullHabit = await db.getHabitById(habit.id);
      if (fullHabit) {
        dispatch({ type: "HABIT_CREATED", habit: fullHabit });
      }
      triggerSync();
    },
    [db, triggerSync],
  );

  const updateHabitAction = useCallback(
    async (id: string, fields: Partial<Omit<Habit, "id" | "created_at">>) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.updateHabit(id, fields, now);
      const updated = await db.getHabitById(id);
      if (updated) {
        dispatch({ type: "HABIT_UPDATED", habit: updated });
      }
      triggerSync();
    },
    [db, triggerSync],
  );

  const archiveHabit = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.archiveHabit(id, new Date().toISOString());
      dispatch({ type: "HABIT_ARCHIVED", habitId: id });
      triggerSync();
    },
    [db, triggerSync],
  );

  const deleteHabit = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.softDeleteHabit(id, new Date().toISOString());
      dispatch({ type: "HABIT_DELETED", habitId: id });
      triggerSync();
    },
    [db, triggerSync],
  );

  const updateSettings = useCallback(
    async (key: string, value: string) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.setSetting(key, value, now);
      const newSettings = { ...state.settings, [key]: value };
      const record = newSettings as Record<string, unknown>;
      if (key === "firstDayOfWeek") {
        record[key] = parseIntSafe(value, DEFAULT_SETTINGS.firstDayOfWeek, 0, 6);
      } else if (key === "dayBoundaryHour") {
        record[key] = parseIntSafe(value, DEFAULT_SETTINGS.dayBoundaryHour, 0, 23);
      } else if (key === "graceDaysPerWeek") {
        record[key] = parseIntSafe(value, DEFAULT_SETTINGS.graceDaysPerWeek, 0, 7);
      }
      if (key === "dayBoundaryHour") {
        localStorage.setItem(BOUNDARY_HOUR_KEY, value);
      }
      // Parse boolean settings
      if (
        key === "showStreaks" ||
        key === "onboardingCompleted" ||
        key === "scoreRevealed" ||
        key === "addMoreDismissed"
      ) {
        record[key] = value === "true";
      }
      // Parse JSON array settings
      if (key === "dismissedMilestones") {
        record[key] = parseDismissedMilestones(value);
      }
      // Light day toggle: update isLightDayToday when today's light day key changes
      if (key.startsWith("lightDay:")) {
        const lightDayDate = key.slice("lightDay:".length);
        const today = getToday();
        if (lightDayDate === today) {
          record["isLightDayToday"] = value === "true";
        }
        // Don't persist the lightDay:* key on the AppSettings object
        delete record[key];
      }
      dispatch({ type: "SETTINGS_CHANGED", settings: newSettings });
      triggerSync();
    },
    [db, state.settings, getToday, triggerSync],
  );

  const updateCompletionNote = useCallback(
    async (habitId: string, note: string | null) => {
      if (!db) return;
      const today = getToday();
      const now = new Date().toISOString();
      await db.updateCompletionNote(habitId, today, note, now);
      dispatch({ type: "UPDATE_NOTE", habitId, effectiveDate: today, note });
      triggerSync();
    },
    [db, getToday, triggerSync],
  );

  const reorderHabits = useCallback(
    async (updates: Array<{ id: string; sort_order: number }>) => {
      if (!db) return;
      const reordered = state.habits.map((h) => {
        const update = updates.find((u) => u.id === h.id);
        return update ? { ...h, sort_order: update.sort_order } : h;
      });
      reordered.sort((a, b) => a.sort_order - b.sort_order);
      const now = new Date().toISOString();
      await db.updateHabitOrder(updates, now);
      dispatch({ type: "REORDER", habits: reordered });
      triggerSync();
    },
    [db, state.habits, triggerSync],
  );

  const createCategory = useCallback(
    async (cat: { name: string; color: string; icon: string }): Promise<Category> => {
      if (!db) throw new Error("Database not ready");
      const id = crypto.randomUUID();
      const sortOrder = state.categories.length;
      const newCat: Omit<Category, "created_at" | "updated_at"> = {
        id,
        name: cat.name,
        color: cat.color,
        icon: cat.icon,
        sort_order: sortOrder,
        deleted_at: null,
      };
      await db.insertCategory(newCat);
      const full = await db.getCategoryById(id);
      const category = full ?? {
        ...newCat,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      dispatch({ type: "CATEGORY_CREATED", category });
      triggerSync();
      return category;
    },
    [db, state.categories.length, triggerSync],
  );

  const updateCategoryAction = useCallback(
    async (id: string, fields: Partial<Pick<Category, "name" | "color" | "icon">>) => {
      if (!db) return;
      const now = new Date().toISOString();
      await db.updateCategory(id, fields, now);
      const updated = await db.getCategoryById(id);
      if (updated) {
        dispatch({ type: "CATEGORY_UPDATED", category: updated });
      }
      triggerSync();
    },
    [db, triggerSync],
  );

  const deleteCategory = useCallback(
    async (id: string) => {
      if (!db) return;
      await db.softDeleteCategory(id, new Date().toISOString());
      dispatch({ type: "CATEGORY_DELETED", categoryId: id });
      triggerSync();
    },
    [db, triggerSync],
  );

  const reorderCategories = useCallback(
    async (updates: Array<{ id: string; sort_order: number }>) => {
      if (!db) return;
      const reordered = state.categories.map((c) => {
        const update = updates.find((u) => u.id === c.id);
        return update ? { ...c, sort_order: update.sort_order } : c;
      });
      reordered.sort((a, b) => a.sort_order - b.sort_order);
      const now = new Date().toISOString();
      await db.updateCategoryOrder(updates, now);
      dispatch({ type: "CATEGORIES_REORDERED", categories: reordered });
      triggerSync();
    },
    [db, state.categories, triggerSync],
  );

  const refresh = useCallback(
    async ({ includeSettings = false }: { includeSettings?: boolean } = {}) => {
      if (!db) return;
      const today = getToday();
      const [habits, completions, categories, settingsRows] = await Promise.all([
        db.getActiveHabits(),
        db.getCompletions(today),
        db.getCategories(),
        includeSettings ? db.getAllSettings() : Promise.resolve(null),
      ]);
      const settings = settingsRows ? parseSettings(settingsRows, today) : undefined;
      if (settings) {
        localStorage.setItem(BOUNDARY_HOUR_KEY, String(settings.dayBoundaryHour));
      }
      dispatch({
        type: "REFRESH",
        habits,
        completions,
        categories,
        settings,
      });
    },
    [db, getToday],
  );

  // Refresh data when sync pulls new data
  useEffect(() => {
    if (lastSyncAt) {
      void refresh();
    }
  }, [lastSyncAt]); // only re-run when lastSyncAt changes (intentionally omitting refresh)

  const value = useMemo<HabitsContextValue>(
    () => ({
      state,
      initializationError,
      retryInitialization,
      completeHabit,
      incrementHabit,
      skipHabit,
      backfillHabit,
      undoCompletion,
      createHabit,
      updateHabit: updateHabitAction,
      archiveHabit,
      deleteHabit,
      updateCompletionNote,
      updateSettings,
      reorderHabits,
      createCategory,
      updateCategory: updateCategoryAction,
      deleteCategory,
      reorderCategories,
      refresh,
    }),
    [
      state,
      initializationError,
      retryInitialization,
      completeHabit,
      incrementHabit,
      skipHabit,
      backfillHabit,
      undoCompletion,
      createHabit,
      updateHabitAction,
      archiveHabit,
      deleteHabit,
      updateCompletionNote,
      updateSettings,
      reorderHabits,
      createCategory,
      updateCategoryAction,
      deleteCategory,
      reorderCategories,
      refresh,
    ],
  );

  return <HabitsContext.Provider value={value}>{children}</HabitsContext.Provider>;
}

export function useHabits(): HabitsContextValue {
  const ctx = useContext(HabitsContext);
  if (!ctx) {
    throw new Error("useHabits must be used within a HabitsProvider");
  }
  return ctx;
}
